import axios, { AxiosRequestConfig } from 'axios';
import { validateSession } from './axiosUtils';
import { COOKIES_KEYS } from '@app/dashboard/constants/cookies-keys';
import { SEARCH_PARAMS_KEYS } from '@app/dashboard/constants/search-params-keys';
import { ONE_MINUTE_MS } from '@app/dashboard/constants/shared';
import { useGlobalStore } from '@app/dashboard/store/global';
import { handleAuthLogout } from '@app/dashboard/utils/manageLocalStorage';
import { getPaymobAppServerUrl } from '@app/dashboard/utils/serverUrls';
import { getLang, getMerchantToken } from '@app/dashboard/utils/localStorageUtils';
import { getCookie } from '@app/dashboard/utils/manageCookies';

const { getState } = useGlobalStore;

const baseUrl = getPaymobAppServerUrl();
const merchantToken = getMerchantToken();
const lang = getLang();
const UNAUTHORIZED_CODE = [401, 403];

const axiosConfig: AxiosRequestConfig = {
  baseURL: baseUrl,
  timeout: ONE_MINUTE_MS,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache',
    'Accept-Language': lang
  }
};

export const axiosPublic = axios.create(axiosConfig);
export const axiosPrivate = axios.create({
  ...axiosConfig,
  headers: {
    ...axiosConfig.headers,
    'Authorization': `Bearer ${merchantToken}`
  }
});

axiosPublic.interceptors.request.use(
  (config) => {
    const currentLang = getLang();
    config.headers['Accept-Language'] = currentLang;
    return config;
  },
  (error) => Promise.reject(error)
);

axiosPrivate.interceptors.request.use(
  async (config) => {
    try {
      const switchDashboardsToken = await getCookie(COOKIES_KEYS.SWITCH_DASHBOARDS_TOKEN);
      const searchParams = new URLSearchParams(location.search);
      const sdi = searchParams.get(SEARCH_PARAMS_KEYS.SWITCH_DASHBOARDS_INTERFACE);
      const switchDashboardAutoLogin = sdi && switchDashboardsToken;

      const validToken = await validateSession();
      const currentLang = getLang();

      if (!validToken && !switchDashboardAutoLogin) {
        getState().setIsAuthenticated(false);
        handleAuthLogout();
      }

      config.headers.Authorization = switchDashboardAutoLogin ? config.headers.Authorization : `Bearer ${validToken}`;
      config.headers['Accept-Language'] = currentLang;
      return config;
    } catch (error) {
      getState().setIsAuthenticated(false);
      handleAuthLogout();
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error)
);

axiosPrivate.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const allow403 = error?.config?.allow403;
    const unauthorizedCodes = allow403 ? [401] : UNAUTHORIZED_CODE;

    if (
      error?.response?.status &&
      unauthorizedCodes.includes(error.response.status)
    ) {
      getState().setIsAuthenticated(false);
      handleAuthLogout();
    }
    return Promise.reject(error);
  }
);
